import type { Venue } from "@/types/api";

/** Solid black placeholder when a venue has no cover, media, images, or logo. */
export const VENUE_IMAGE_FALLBACK =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='4' height='3' viewBox='0 0 4 3'%3E%3Crect width='4' height='3' fill='%23000'/%3E%3C/svg%3E";

export function isVenueImageFallback(url: string | undefined | null): boolean {
  return !url || url === VENUE_IMAGE_FALLBACK;
}

/** Two-letter abbreviation used when a venue has no logo (`Marquee NYC` -> `MN`). */
export function venueInitials(name: string | null | undefined): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/**
 * API payloads often mix strings, `{ url }`, or other shapes; align with VenueDetail.extractImages.
 */
function pickImageUrl(raw: unknown): string | undefined {
  if (raw == null) return undefined;
  if (typeof raw === "string") {
    const t = raw.trim();
    return t.length > 0 ? t : undefined;
  }
  if (typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    for (const k of ["url", "href", "src", "path"] as const) {
      const val = o[k];
      if (typeof val === "string" && val.trim().length > 0) return val.trim();
    }
  }
  return undefined;
}

/** Thumbnail / card: cover → each media item → each images item → logo → bundled fallback. */
export function getVenuePrimaryImageUrl(v: Venue | null | undefined): string {
  if (!v) return VENUE_IMAGE_FALLBACK;

  const c = pickImageUrl(v.cover);
  if (c) return c;

  if (v.media?.length) {
    for (const m of v.media as unknown[]) {
      const u = pickImageUrl(m);
      if (u) return u;
    }
  }

  if (v.images?.length) {
    for (const x of v.images as unknown[]) {
      const u = pickImageUrl(x);
      if (u) return u;
    }
  }

  const lg = pickImageUrl(v.logo);
  if (lg) return lg;
  return VENUE_IMAGE_FALLBACK;
}

/**
 * Photo URLs for carousels only (no logo — logos are branding, not venue shoots).
 * Order: cover, media, images, optional room thumbnails — same idea as VenueDetail.extractImages.
 */
export function getVenueImageGalleryUrls(v: Venue | null | undefined): string[] {
  if (!v) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (s: string | undefined) => {
    if (!s || seen.has(s)) return;
    seen.add(s);
    out.push(s);
  };
  const addRaw = (raw: unknown) => push(pickImageUrl(raw));

  addRaw(v.cover);
  if (v.media?.length) {
    for (const m of v.media as unknown[]) {
      addRaw(m);
    }
  }
  if (v.images?.length) {
    for (const x of v.images as unknown[]) {
      addRaw(x);
    }
  }
  if (v.rooms?.length) {
    for (const r of v.rooms) {
      addRaw(r?.image);
      if (Array.isArray(r?.images)) {
        for (const x of r.images) addRaw(x);
      }
    }
  }

  return out;
}

/** Max carousel photos bundled into map/search views (avoid huge payloads & main-thread churn). */
export const MAP_SEARCH_GALLERY_CAP = 12;

const API_ORIGIN = new URL(import.meta.env.VITE_API_BASE_URL || "https://api.venuelista.com/api").origin;
/** Must match ImageThumbController::WIDTHS in the API. */
const API_THUMB_WIDTHS = [160, 480, 800, 1200];

/**
 * Shrink an image URL for grids/maps:
 * - images.unsplash.com: width query params.
 * - our API's /img/... originals: /thumb/{w}/img/....webp (resized WebP, cached on disk).
 * Anything else is returned unchanged.
 */
export function thumbnailDisplayUrl(url: string, maxWidth?: number): string;
export function thumbnailDisplayUrl(url: string | null | undefined, maxWidth?: number): string | null | undefined;
export function thumbnailDisplayUrl(
  url: string | null | undefined,
  maxWidth = 640
): string | null | undefined {
  if (!url) return url;
  const t = url.trim();
  if (isVenueImageFallback(t) || /^data:|^blob:/i.test(t)) return t;
  if (!/^https?:\/\//i.test(t)) return url;

  try {
    const u = new URL(t);
    if (
      u.hostname === "images.unsplash.com" ||
      u.hostname === "plus.unsplash.com"
    ) {
      const w = Math.min(Math.round(maxWidth), 1920);
      u.searchParams.set("w", String(w));
      u.searchParams.set("q", "75");
      u.searchParams.set("auto", "format");
      return u.toString();
    }
    if (u.origin === API_ORIGIN && /^\/img\/.+\.(jpe?g|png|webp|gif)$/i.test(u.pathname)) {
      // Snap up to a server bucket so each image has at most 4 cached sizes.
      const w = API_THUMB_WIDTHS.find((b) => b >= maxWidth) ?? API_THUMB_WIDTHS[API_THUMB_WIDTHS.length - 1];
      return `${u.origin}/thumb/${w}${u.pathname}.webp`;
    }
    if (u.origin === API_ORIGIN && /^\/api\/member\/photos\/[^/]+\/file$/.test(u.pathname)) {
      // Signed gallery photo — the API resizes when given one of the same buckets (`w` is excluded from the signature).
      const w = API_THUMB_WIDTHS.find((b) => b >= maxWidth) ?? API_THUMB_WIDTHS[API_THUMB_WIDTHS.length - 1];
      u.searchParams.set("w", String(w));
      return u.toString();
    }
  } catch {
    /* ignore malformed */
  }

  return t;
}

/**
 * `srcSet` for an image already passed through thumbnailDisplayUrl, so the browser picks
 * 480w on 1x screens and 800w on retina. Undefined for anything that isn't an API thumb.
 */
export function thumbnailSrcSet(url: string | undefined): string | undefined {
  const m = url?.match(/^(.*\/thumb\/)\d+(\/img\/.+\.webp)$/);
  return m ? `${m[1]}480${m[2]} 480w, ${m[1]}800${m[2]} 800w` : undefined;
}
